import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDigest, useGrowth, useTickets, useUsage } from '@/hooks/useConsoleData';
import { Section } from '@/components/console/Section';
import { StatusStrip } from '@/components/console/StatusStrip';
import { PersonCard } from '@/components/console/PersonCard';
import { FailedSessionsList } from '@/pages/console/FailedSessionsPage';
import { ReliabilityTodaySection } from '@/pages/console/ReliabilityPage';
import { useFailedSessions } from '@/hooks/useConsoleData';
import { Link } from 'react-router-dom';
import { ago, num, when } from '@/lib/format';
import { WRITE_REASON_LABEL, WRITE_REASON_WHY, actionItems, readDoneKeys, statusStrip, writeDoneKeys, type ActionItem, type WriteReason } from '@/lib/today';
import s from './console.module.css';

/**
 * Today: the status strip, then the checklist ordered by value, then the
 * digest's proposals. Every number is a row count the server took; every
 * row on the list is a person or a ticket that exists.
 */
const SHOW_FIRST = 12;

export default function TodayPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const growth = useGrowth();
  const digest = useDigest();
  const tickets = useTickets('new');
  const failed = useFailedSessions('24h');
  const [doneKeys, setDoneKeys] = useState<Set<string>>(() => readDoneKeys());
  const [showAll, setShowAll] = useState(false);

  const items = useMemo(
    () => (growth.data ? actionItems({ people: growth.data.recentSignups, tickets: tickets.data?.tickets ?? [], doneKeys }) : []),
    [growth.data, tickets.data, doneKeys],
  );
  const open = items.filter((i) => !i.done);
  const done = items.filter((i) => i.done);
  const shown = showAll ? open : open.slice(0, SHOW_FIRST);
  const usage = useUsage(shown.filter((i): i is Exclude<ActionItem, { kind: 'ticket' }> => i.kind !== 'ticket').map((i) => i.person.userId));

  const tick = (id: string, on: boolean) => {
    const next = new Set(doneKeys);
    if (on) next.add(id); else next.delete(id);
    setDoneKeys(next);
    writeDoneKeys(next);
  };
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['growth'] }); void qc.invalidateQueries({ queryKey: ['messages'] }); };
  const journal = (userId: string) => navigate(`/people?user=${encodeURIComponent(userId)}&journal=1`);

  let lastGroup: string | null = null;
  const groupOf = (i: ActionItem): string => (i.kind === 'write' ? WRITE_REASON_LABEL[i.reason] : i.kind === 'ticket' ? 'Unanswered support tickets' : 'Went to checkout, did not pay');
  const groupWhy = (i: ActionItem): string => (i.kind === 'write' ? WRITE_REASON_WHY[i.reason as WriteReason] : i.kind === 'ticket' ? 'Answer from GHL; the ticket is a card on their contact.' : 'They opened checkout and did not finish. Ask what went wrong; nothing they did is lost.');

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <h1 className={s.pageTitle}>Today</h1>
        <span className={s.pageSub}>{open.length} to do · {done.length} done today</span>
      </div>

      {digest.isError && <div className={s.error}>The digest did not load. The strip needs GET /provider/admin/digest.</div>}
      {digest.data
        ? <StatusStrip rows={statusStrip(digest.data)} generatedAt={digest.data.generatedAt} />
        : !digest.isError && <div className={s.sub}>Measuring the week.</div>}

      <ReliabilityTodaySection />

      <Section title="Sessions that did not finish since yesterday" summary={failed.data ? (failed.data.traces.length ? 'every engine session whose trace ended any way but completed, newest first; open one to see both lanes' : 'every session that posted a trace finished') : 'reading the traces'} count={failed.data ? failed.data.traces.length : null} defaultOpen={!!failed.data && failed.data.traces.length > 0} id="failed-sessions">
        <FailedSessionsList since="24h" />
        <div className={s.sub} style={{ marginTop: 8 }}><Link to="/sessions/failed?since=7d">A wider window</Link></div>
      </Section>

      {digest.data && (
        <Section title="Proposals" summary={digest.data.proposals.length ? 'from the digest rules, each with its evidence' : 'nothing stands out today'} count={digest.data.proposals.length} defaultOpen={digest.data.proposals.length > 0}>
          {digest.data.proposals.length === 0 && <div className={s.empty}>Nothing stands out today.</div>}
          <div className={s.list}>
            {digest.data.proposals.map((p, n) => (
              <div key={p.rule} className={s.proposal}>
                <span className={s.proposalN}>{n + 1}</span>
                <div>
                  {p.text}
                  <div className={s.sub}>{Object.entries(p.evidence).map(([k, v]) => `${k} ${v === null ? 'not measured' : v}`).join(' · ')}</div>
                </div>
              </div>
            ))}
          </div>
          {digest.data.stops.total > 0 && (
            <div className={s.sub} style={{ marginTop: 10 }}>
              Stopped: {digest.data.stops.total} ({digest.data.stops.cohort}): {digest.data.stops.buckets.filter((b) => b.count > 0).map((b) => `${b.step.replace(/_/g, ' ')} ${b.count}`).join(', ')}.
            </div>
          )}
        </Section>
      )}

      <div>
        <div className={s.pageHead}>
          <h2 className={s.pageTitle} style={{ fontSize: '1.1rem' }}>Action items</h2>
          <span className={s.pageSub}>warmest first · tick when done, or send a note and it ticks itself</span>
        </div>
        {growth.isError && <div className={s.error}>Could not load the people. The list needs GET /provider/admin/growth.</div>}
        {tickets.isError && <div className={s.notice}>Tickets did not load (GET /provider/admin/support-tickets), so the list has no tickets in it.</div>}
        {growth.isLoading && <div className={s.sub}>Loading the people.</div>}
        {growth.data && open.length === 0 && <div className={s.empty}>Nothing open. Every note is sent and every ticket answered.</div>}
        <div className={s.actions}>
          {shown.map((i) => {
            const group = groupOf(i);
            const head = group !== lastGroup;
            lastGroup = group;
            return (
              <div key={i.id}>
                {head && (
                  <div className={s.groupHead}>
                    <span className={s.groupLabel}>{group}</span>
                    <span className={s.sub}>{groupWhy(i)}</span>
                  </div>
                )}
                {i.kind === 'ticket' ? (
                  <div className={s.action}>
                    <div className={s.actionHead}>
                      <input type="checkbox" className={s.tick} checked={i.done} onChange={(e) => tick(i.id, e.target.checked)} aria-label="Done" />
                      <span className={s.actionTitle}>{i.ticket.subject}</span>
                      <span className={s.actionWhy}>{i.ticket.name ?? i.ticket.email ?? 'no account'} · {i.ticket.source.replace('_', ' ')} · opened {ago(i.ticket.createdAt)} ({num(i.ticket.ageHours)} h)</span>
                      <span className={s.actionLinks}>
                        {i.ticket.ghlConversationUrl && <a className={s.linkA} href={i.ticket.ghlConversationUrl} target="_blank" rel="noreferrer">Answer in GHL</a>}
                        {i.ticket.ghlContactUrl && <a className={s.linkA} href={i.ticket.ghlContactUrl} target="_blank" rel="noreferrer">Contact</a>}
                        {i.ticket.userId && <button type="button" className={s.linkBtn} onClick={() => journal(i.ticket.userId as string)}>Journal</button>}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className={`${s.action} ${i.done ? s.actionDone : ''}`}>
                    <div className={s.actionHead}>
                      <input type="checkbox" className={s.tick} checked={i.done} onChange={(e) => tick(i.id, e.target.checked)} aria-label="Done" />
                      <span className={s.actionWhy}>{i.kind === 'write' ? WRITE_REASON_WHY[i.reason] : 'Opened checkout, did not pay.'}</span>
                    </div>
                    <PersonCard p={i.person} usage={usage.data ? usage.data.people[i.person.userId] ?? null : undefined} payment={growth.data?.recentPayments.find((pay) => pay.userId === i.person.userId) ?? null} open={false} onSent={() => { tick(i.id, true); refresh(); }} onJournal={journal} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
        {open.length > SHOW_FIRST && (
          <div style={{ marginTop: 10 }}>
            <button type="button" className={s.filter} onClick={() => setShowAll((v) => !v)}>{showAll ? `Show the first ${SHOW_FIRST}` : `Show all ${open.length}`}</button>
          </div>
        )}
      </div>

      <Section title="Done today" summary={done.length ? 'sent or ticked off in this browser today' : 'nothing yet'} count={done.length}>
        {done.length === 0 && <div className={s.empty}>Nothing done yet today.</div>}
        <div className={s.list}>
          {done.map((i) => (
            <div key={i.id} className={`${s.action} ${s.actionDone}`}>
              <div className={s.actionHead}>
                <input type="checkbox" className={s.tick} checked onChange={() => tick(i.id, false)} aria-label="Not done" />
                <span className={s.actionTitle}>{i.kind === 'ticket' ? i.ticket.subject : i.person.name || i.person.email}</span>
                <span className={s.actionWhy}>{i.kind === 'ticket' ? 'ticket' : i.kind === 'write' ? WRITE_REASON_LABEL[i.reason] : 'checkout'}{i.kind !== 'ticket' && i.person.outreach.length ? ` · last note ${when(i.person.outreach[i.person.outreach.length - 1].at)}` : ''}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
